import { useState, type FormEvent } from 'react';
import { Plus } from 'lucide-react';
import { Async, EmptyState, PageHeader } from '../../components/ui/States';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { useUser } from '../../auth/AuthContext';
import { canManage } from '../../auth/roles';
import { api, ApiError } from '../../lib/api';
import { errorMessage, useApi } from '../../hooks/useApi';
import { btnGhost, btnPrimary, cardCls, ConfirmModal, formatDate, inputCls } from './shared';

interface Tenant {
  id: string;
  slug: string;
  name: string;
  status: 'pending' | 'active' | 'suspended' | 'cancelled';
  defaultCurrency: string;
  createdAt: string;
}

const STATUS: Record<Tenant['status'], { label: string; variant: 'success' | 'warning' | 'danger' }> = {
  active: { label: 'Active', variant: 'success' },
  pending: { label: 'Pending', variant: 'warning' },
  suspended: { label: 'Suspended', variant: 'danger' },
  cancelled: { label: 'Cancelled', variant: 'danger' },
};

export function Tenants() {
  const user = useUser();
  const manage = canManage(user.role);
  const state = useApi<{ tenants: Tenant[] }>('/admin/tenants');

  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [currency, setCurrency] = useState('');
  const [pending, setPending] = useState<Tenant | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const closeAdd = () => {
    setAdding(false);
    setError(null);
    setName('');
    setSlug('');
    setCurrency('');
  };

  const submitAdd = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const body: { slug: string; name: string; defaultCurrency?: string } = { slug: slug.trim(), name: name.trim() };
      if (currency.trim()) body.defaultCurrency = currency.trim().toUpperCase();
      await api('/admin/tenants', { method: 'POST', body });
      closeAdd();
      state.reload();
    } catch (err) {
      setError(err instanceof ApiError && err.status === 409 ? 'An app with this address already exists.' : errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async () => {
    if (!pending) return;
    setBusy(true);
    setError(null);
    try {
      await api(`/admin/tenants/${pending.id}`, { method: 'PATCH', body: { status: pending.status === 'active' ? 'suspended' : 'active' } });
      setPending(null);
      state.reload();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const suspending = pending?.status === 'active';

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <PageHeader
        title="Apps"
        subtitle="The apps that rent the platform. Suspending an app stops it from working until you reactivate it."
        actions={
          manage && (
            <button onClick={() => setAdding(true)} className={`${btnPrimary} flex items-center gap-1.5`}>
              <Plus className="w-3.5 h-3.5" /> Add app
            </button>
          )
        }
      />
      <Async state={state}>
        {({ tenants }) =>
          tenants.length === 0 ? (
            <EmptyState title="No apps yet" message={manage ? 'Add the first app that will use the platform.' : 'Apps will appear here once an admin adds them.'} />
          ) : (
            <div className={`${cardCls} overflow-x-auto`}>
              <table className="w-full text-left text-xs">
                <thead className="text-gray-400 border-b border-[#241b33]">
                  <tr>
                    <th className="px-4 py-3 font-medium">App</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Currency</th>
                    <th className="px-4 py-3 font-medium">Added</th>
                    {manage && <th className="px-4 py-3 font-medium text-right">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#241b33]">
                  {tenants.map((t) => (
                    <tr key={t.id}>
                      <td className="px-4 py-3">
                        <p className="text-sm font-semibold text-white">{t.name}</p>
                        <p className="text-gray-400 font-mono">{t.slug}</p>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={STATUS[t.status].variant}>{STATUS[t.status].label}</Badge>
                      </td>
                      <td className="px-4 py-3 text-gray-200">{t.defaultCurrency}</td>
                      <td className="px-4 py-3 text-gray-300">{formatDate(t.createdAt)}</td>
                      {manage && (
                        <td className="px-4 py-3 text-right">
                          {t.status === 'cancelled' ? (
                            <span className="text-gray-500">—</span>
                          ) : (
                            <button
                              className={btnGhost}
                              onClick={() => {
                                setError(null);
                                setPending(t);
                              }}
                            >
                              {t.status === 'active' ? 'Suspend' : t.status === 'pending' ? 'Activate' : 'Reactivate'}
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </Async>

      <Modal open={adding} onOpenChange={(o) => !o && !busy && closeAdd()} title="Add app" maxWidth="md">
        <form onSubmit={submitAdd} className="space-y-4">
          <div>
            <label htmlFor="t-name" className="block text-xs font-semibold text-gray-300 mb-1">App name</label>
            <input id="t-name" required value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label htmlFor="t-slug" className="block text-xs font-semibold text-gray-300 mb-1">Short address (letters, numbers, dashes)</label>
            <input id="t-slug" required pattern="[a-z0-9-]+" value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} className={inputCls} />
          </div>
          <div>
            <label htmlFor="t-cur" className="block text-xs font-semibold text-gray-300 mb-1">Currency code (optional, e.g. USD)</label>
            <input id="t-cur" maxLength={3} value={currency} onChange={(e) => setCurrency(e.target.value)} className={inputCls} />
          </div>
          {error && <p className="text-xs text-rose-400" role="alert">{error}</p>}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={closeAdd} disabled={busy} className={btnGhost}>Cancel</button>
            <button type="submit" disabled={busy} className={btnPrimary}>{busy ? 'Adding…' : 'Add app'}</button>
          </div>
        </form>
      </Modal>

      <ConfirmModal
        open={pending !== null}
        title={suspending ? `Suspend ${pending?.name}?` : `Reactivate ${pending?.name}?`}
        description={suspending ? 'The app will stop working until you reactivate it.' : 'The app will start working again.'}
        confirmLabel={suspending ? 'Suspend app' : 'Reactivate app'}
        danger={suspending}
        busy={busy}
        error={error}
        onConfirm={toggle}
        onClose={() => setPending(null)}
      />
    </div>
  );
}
